// ABA note templates used by the RBT Write Notes flow.
// noteData is stored flat: { sessionSummary, parentCommunication, ...templateFieldValues }
// Field keys here MUST match functions/src/generateNote.ts so AI output maps 1:1.

export interface NoteField {
  key: string;
  label: string;
  placeholder?: string;
  rows?: number;
}

export interface NoteTemplateDef {
  id: string;
  label: string;
  description: string;
  fields: NoteField[];
}

// Shown for every template.
export const COMMON_FIELDS: NoteField[] = [
  {
    key: 'sessionSummary',
    label: 'Session Summary',
    placeholder: 'Overall summary of the session…',
    rows: 4,
  },
  {
    key: 'parentCommunication',
    label: 'Parent Communication',
    placeholder: 'What was shared with the caregiver…',
    rows: 3,
  },
];

export const NOTE_TEMPLATES: NoteTemplateDef[] = [
  {
    id: 'SOAP',
    label: 'SOAP',
    description: 'Subjective · Objective · Assessment · Plan',
    fields: [
      { key: 'subjective', label: 'Subjective', placeholder: 'Caregiver report, context, mood…', rows: 3 },
      { key: 'objective', label: 'Objective', placeholder: 'Observable, measurable data…', rows: 3 },
      { key: 'assessment', label: 'Assessment', placeholder: 'Clinical interpretation of the data…', rows: 3 },
      { key: 'plan', label: 'Plan', placeholder: 'Next steps, adjustments…', rows: 3 },
    ],
  },
  {
    id: 'ABC',
    label: 'ABC',
    description: 'Antecedent · Behavior · Consequence',
    fields: [
      { key: 'antecedent', label: 'Antecedent', placeholder: 'What happened before the behavior…', rows: 3 },
      { key: 'behavior', label: 'Behavior', placeholder: 'Operationally defined behavior…', rows: 3 },
      { key: 'consequence', label: 'Consequence', placeholder: 'What happened after the behavior…', rows: 3 },
      { key: 'frequencyDuration', label: 'Frequency / Duration', placeholder: 'Counts, rates, durations…', rows: 2 },
      { key: 'patterns', label: 'Patterns', placeholder: 'Hypothesized function, patterns observed…', rows: 2 },
    ],
  },
  {
    id: 'DTT',
    label: 'DTT',
    description: 'Discrete Trial Training',
    fields: [
      { key: 'programsRun', label: 'Programs Run', placeholder: 'Programs targeted this session…', rows: 2 },
      { key: 'trialData', label: 'Trial Data', placeholder: 'Trials run, correct/incorrect, percentages…', rows: 3 },
      { key: 'promptLevels', label: 'Prompt Levels', placeholder: 'Independent, gestural, partial/full physical…', rows: 2 },
      { key: 'masteryCriteria', label: 'Mastery Criteria', placeholder: 'Targets mastered / mastery progress…', rows: 2 },
      { key: 'errorPatterns', label: 'Error Patterns', placeholder: 'Error patterns and corrections…', rows: 2 },
    ],
  },
  {
    id: 'NET',
    label: 'NET',
    description: 'Natural Environment Training',
    fields: [
      { key: 'naturalEnvironment', label: 'Natural Environment', placeholder: 'Setting / natural context used…', rows: 2 },
      { key: 'teachingOpportunities', label: 'Teaching Opportunities', placeholder: 'Embedded teaching opportunities…', rows: 3 },
      { key: 'clientResponse', label: 'Client Response', placeholder: "Client's responses and engagement…", rows: 3 },
      { key: 'reinforcement', label: 'Reinforcement', placeholder: 'Reinforcers used and delivery…', rows: 2 },
      { key: 'generalization', label: 'Generalization', placeholder: 'Generalization across people/settings…', rows: 2 },
    ],
  },
  {
    id: 'Progress',
    label: 'Progress',
    description: 'Goal-by-goal progress review',
    fields: [
      { key: 'progressSummary', label: 'Progress Summary', placeholder: 'Progress toward goals…', rows: 4 },
      { key: 'goalsAddressed', label: 'Goals Addressed', placeholder: 'Treatment plan goals addressed…', rows: 3 },
      { key: 'barriersToProgress', label: 'Barriers to Progress', placeholder: 'Barriers encountered…', rows: 2 },
      { key: 'modifications', label: 'Modifications', placeholder: 'Program modifications made…', rows: 2 },
      { key: 'nextSteps', label: 'Next Steps', placeholder: 'Recommended next steps…', rows: 3 },
    ],
  },
];

export const DEFAULT_TEMPLATE_ID = 'SOAP';

export const getTemplate = (id: string | undefined): NoteTemplateDef =>
  NOTE_TEMPLATES.find((t) => t.id === id) || NOTE_TEMPLATES[0];

// All fields (common + template-specific) for a given template id.
export const fieldsFor = (id: string | undefined): NoteField[] => [
  ...COMMON_FIELDS,
  ...getTemplate(id).fields,
];
